import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';

/**
 * Deleting one activation, shared by the list row's ⋯ menu and the detail page's Delete.
 *
 * One hook rather than the same mutation written twice, because the interesting part is not the call
 * — it is what `deleted: false` means, and two copies of that would drift. The remote half is
 * best-effort: a read-only RudderStack link, or a refusal from config-backend, leaves the activation
 * exactly where it was. Reporting that as success would be a lie the user acts on, so it is surfaced
 * as an error toast even though the request itself succeeded.
 *
 * `onDeleted` is where the two callers differ, and the only place they should: the list stays put and
 * lets the invalidation redraw it, while the detail page has to leave — it is addressing a connection
 * that no longer exists.
 */
export function useDeleteActivation(onDeleted?: () => void): {
  remove: (syncId: string) => void;
  isPending: boolean;
} {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const signIn = useRudderstackSignInAgain();

  const mutation = useMutation(
    orpc.audience.deleteActivation.mutationOptions({
      onSuccess: (result) => {
        if (!result.deleted) {
          toast.error('Could not delete this activation', {
            description:
              'RudderStack refused the delete, or this workspace’s link is read-only. Nothing was removed — the activation is still syncing.',
          });
          return;
        }

        // Every read in the namespace is now stale: the syncs fan-out, the meta rows and the
        // destination projection alike. Same invalidation the Activate sheet does on the way in.
        void queryClient.invalidateQueries({ queryKey: orpc.activations.key() });
        // ACT2-640: audience.list carries activationCount — drop it too so the list chip does not
        // keep showing a deleted activation until staleTime expires.
        void queryClient.invalidateQueries({ queryKey: orpc.audience.list.key() });
        toast.success('Activation deleted', {
          description:
            'The audience already in the destination is not removed — delete it there if you no longer want it.',
        });
        onDeleted?.();
      },
      onError: (err: Error) => {
        if (signIn.toastIfReauth(err)) return;
        toast.error('Could not delete this activation', { description: err.message });
      },
    }),
  );

  return {
    remove: (syncId: string) => mutation.mutate({ syncId }),
    isPending: mutation.isPending,
  };
}
